import React, { Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    AddEditCustomReportErrors,
    AddEditCustomReportState,
    clearColumnsByTabType,
    containsTab,
    CustomReportPage,
    getNotMatchingTabs,
    getTab,
} from '../../excel-listings/custom-report-helpers';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import { InfoField } from '@components-clean/core/info-field';
import { useTranslation } from 'hooks';
import {
    ReportDefinitionFields,
    ReportDefinitionFields_ReportDefinition_tabs_ReportDefinitionTab as ReportDefinitionFields_tabs,
} from 'queries/generated/fragments.generated';
import { ReportDefinitionTabs } from 'types/graphql-global-types';

interface CustomReportStep2DataProps {
    show: boolean;
    optionsTabs: ReportDefinitionFields_tabs[];
    reportDefinition: ReportDefinitionFields;
    state: AddEditCustomReportState;
    setState: Dispatch<SetStateAction<AddEditCustomReportState>>;
    getDefaultOptionsTab: (tabType: ReportDefinitionTabs) => ReportDefinitionFields_tabs;
    reset: () => void;
    updateErrorState: (errorState: AddEditCustomReportErrors, pageNumber: number) => void;
}

// TODO: refactor to use ref as a prop - forwardRef was deprecated in React 19
// Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
// eslint-disable-next-line @eslint-react/no-forward-ref
const CustomReportStep2Data = forwardRef(
    (
        {
            show,
            optionsTabs,
            reportDefinition,
            state,
            setState,
            getDefaultOptionsTab,
            reset,
            updateErrorState,
        }: CustomReportStep2DataProps,
        ref,
    ) => {
        const { t } = useTranslation();
        const { t: tDialog } = useTranslation('ram', {
            keyPrefix: 'reporting.excelListings.dialogs.addEditExcelListingsConfig',
        });

        const initialErrors: AddEditCustomReportErrors = {
            tabs: '',
        };

        const reportDefinitionTabs = state.reportDefinition.tabs ?? [];
        const reportDefinitionTabsLength = reportDefinitionTabs.length ?? 0;
        const currentLeftItemsRef = useRef<DragDropListItemProps[]>([]);
        const currentRightItemsRef = useRef<DragDropListItemProps[]>([]);
        const currentSelectedItemRef = useRef<DragDropListItemProps>(null);

        const [errors, setErrors] = useState({ ...initialErrors } as AddEditCustomReportErrors);
        const [notSelectedTabs, setNotSelectedTabs] = useState([] as ReportDefinitionFields_tabs[]);

        const getNotSelectedTabs = () =>
            optionsTabs.filter(
                (optionsTab: ReportDefinitionFields_tabs) => !containsTab(reportDefinitionTabs, optionsTab.tabType),
            );

        function validationOnUpdate() {
            if (reportDefinitionTabsLength > 0) {
                setErrors({ tabs: '' });
                return true;
            }

            setErrors({ tabs: t('reports:customReportNoTabsSelectedError') });
            return false;
        }

        useImperativeHandle(ref, () => ({
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        const leftListActiveItems = currentLeftItemsRef.current.filter((i) => i.selected);
        const rightListActiveItems = currentRightItemsRef.current.filter((i) => i.selected);

        const getListItems = (inputTabs: (ReportDefinitionFields_tabs | null)[] | null) =>
            inputTabs?.map(
                (tab, index) =>
                    ({
                        key: tab?.tabType,
                        title: t(`reports:${tab?.tabType}`),
                        index,
                        selected:
                            leftListActiveItems.find((Item) => Item.key === tab?.tabType) !== undefined ||
                            rightListActiveItems.find((Item) => Item.key === tab?.tabType) !== undefined,
                    }) as DragDropListItemProps,
            ) ?? [];

        const getUnchangedReportDefinitionTabs = (
            listItems: DragDropListItemProps[],
        ): ReportDefinitionFields_tabs[] => {
            const unchangedListItems = listItems.filter((item: DragDropListItemProps) =>
                containsTab(reportDefinition.tabs, item.key as ReportDefinitionTabs),
            );

            return unchangedListItems.map((listItem) => {
                const tabType = listItem.key as ReportDefinitionTabs;
                const tab = getTab(reportDefinition.tabs, tabType);

                return {
                    __typename: 'ReportDefinitionTab',
                    tabType,
                    columns: [...(tab?.columns ?? [])],
                } as ReportDefinitionFields_tabs;
            });
        };

        const addNewTab = (
            tabType: ReportDefinitionTabs,
            updatedTabs: ReportDefinitionFields_tabs[],
            index: number,
            updatedOptionsTabs: ReportDefinitionFields_tabs[],
        ) => {
            const newTab = getDefaultOptionsTab(tabType);
            updatedTabs.splice(index, 0, newTab);
            clearColumnsByTabType(updatedOptionsTabs, tabType);
        };

        const updateTabs = (
            list2Items: DragDropListItemProps[],
            updatedTabs: ReportDefinitionFields_tabs[],
            updatedOptionsTabs: ReportDefinitionFields_tabs[],
        ) => {
            list2Items.forEach((item: DragDropListItemProps, index: number) => {
                const tabType = item.key as ReportDefinitionTabs;

                if (!containsTab(updatedTabs, tabType)) {
                    addNewTab(tabType, updatedTabs, index, updatedOptionsTabs);
                }
            });
        };

        const getUnchangedOptionsTabs = (list1Items: DragDropListItemProps[]) => {
            const tabs = list1Items.map((item: DragDropListItemProps) =>
                getDefaultOptionsTab(item.key as ReportDefinitionTabs),
            );

            const selectedOptionsTabs = getNotMatchingTabs(optionsTabs, tabs);

            return [...tabs, ...selectedOptionsTabs];
        };

        const updateState = (list1Items: DragDropListItemProps[], list2Items: DragDropListItemProps[]) => {
            const updatedTabs = getUnchangedReportDefinitionTabs(list2Items);
            const updatedOptionsTabs = getUnchangedOptionsTabs(list1Items);

            updateTabs(list2Items, updatedTabs, updatedOptionsTabs);

            setState({
                ...state,
                reportDefinition: {
                    ...state.reportDefinition,
                    tabs: updatedTabs,
                },
                optionsTabs: updatedOptionsTabs,
            });
        };

        const update = (list1Items: DragDropListItemProps[], list2Items: DragDropListItemProps[]) => {
            updateState(list1Items, list2Items);
        };

        useEffect(() => {
            updateErrorState(errors, CustomReportPage.step2Data);
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [errors]);

        useEffect(() => {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setNotSelectedTabs(getNotSelectedTabs());
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [optionsTabs]);

        useEffect(() => {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            if (reportDefinitionTabsLength > 0) setErrors({ tabs: t('') });
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [reportDefinitionTabs]);

        return (
            <>
                {show && (
                    <div className="step2-data k-p-3">
                        <div style={{ height: errors.tabs && errors.tabs !== '' ? '385px' : '100%' }}>
                            <DragDropListsCore
                                list1Items={getListItems(reportDefinition.tabs)}
                                list2Items={getListItems(notSelectedTabs)}
                                list1Header={tDialog('steps.2.selected')}
                                list2Header={tDialog('steps.2.available')}
                                onChangeItemsLeft={(items) => {
                                    currentLeftItemsRef.current = items;
                                }}
                                onChangeItemsRight={(items) => {
                                    currentRightItemsRef.current = items;
                                    update(items, [...currentLeftItemsRef.current]);
                                }}
                                idField="key"
                                isTwoLists
                                handleReset={reset}
                                optionalItemClickCallback={(event) => {
                                    currentSelectedItemRef.current = event.dataItem;
                                }}
                            />
                        </div>
                        {errors.tabs && errors.tabs !== '' && (
                            <div className="k-mt-4">
                                <InfoField type="Warning" message={errors.tabs} />
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    },
);

export default CustomReportStep2Data;
